import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BrainIcon, LightbulbIcon, MagnifyingGlassIcon, PulseIcon } from "@phosphor-icons/react";

/**
 * v6.5.53 - Brain Insights page.
 * Renders (a) the 44+ mined empirical rules grouped by family, and
 * (b) recent brain-generated strategies. This is the "what has my brain
 * learned across cycles" surface.
 */
export default function BrainInsights() {
  const [params, setParams] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [mining, setMining] = useState(false);
  const [mineReport, setMineReport] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");

  const load = () => {
    setErr(null);
    Promise.all([
      api.get("/brain/parameters").then((r) => r.data),
      api.get("/admin/brain/strategies?limit=30").then((r) => r.data),
    ])
      .then(([p, s]) => {
        setParams(p);
        setStrategies(s.strategies || []);
      })
      .catch((e) => setErr(e?.message || "load failed"));
  };
  useEffect(() => { load(); }, []);

  const runMine = () => {
    setMining(true);
    setMineReport(null);
    api.post("/admin/brain/mine")
      .then((r) => { setMineReport(r.data); load(); })
      .catch((e) => setErr(e?.message || "mining failed"))
      .finally(() => setMining(false));
  };

  const allRules = [];
  if (params) {
    for (const [fam, ps] of Object.entries(params)) {
      if (fam.startsWith("_")) continue;
      for (const [pname, meta] of Object.entries(ps)) {
        if (pname.startsWith("_") || !meta.empirical_rules) continue;
        for (const r of meta.empirical_rules) {
          allRules.push({ family: fam, param: pname, ...r });
        }
      }
    }
  }
  const filteredRules = q
    ? allRules.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()))
    : allRules;
  const byFamily = {};
  for (const r of filteredRules) {
    (byFamily[r.family] = byFamily[r.family] || []).push(r);
  }

  return (
    <div className="p-6 space-y-8" data-testid="brain-insights-page">
      <header className="flex items-center gap-3">
        <BrainIcon size={28} color="#8B5CF6" weight="fill" />
        <div>
          <h1 className="text-2xl mono">Brain Insights</h1>
          <p className="text-sm text-[#94A3B8] mono">
            What the Parameter Brain has learned across all cycles.
          </p>
        </div>
        <div className="flex-1" />
        <button
          data-testid="btn-run-mine"
          onClick={runMine}
          disabled={mining}
          className="px-4 py-2 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white mono text-sm rounded disabled:opacity-50"
        >
          {mining ? "Mining..." : "Re-mine now"}
        </button>
      </header>

      {err && <div className="text-[#FF4D4D] mono text-sm" data-testid="brain-err">{err}</div>}
      {mineReport && (
        <div className="p-3 bg-[#15171E] border border-[#2A2D3A] rounded text-sm mono" data-testid="mine-report">
          <span className="text-[#2ECA7F]">Mining complete:</span>{" "}
          {mineReport.total_bots} bots scanned · {mineReport.groups} groups ·{" "}
          <span className="text-[#8B5CF6]">{mineReport.rules_added} rules refreshed</span>
        </div>
      )}

      <section>
        <div className="flex items-center gap-2 mb-3">
          <LightbulbIcon size={20} color="#FBBF24" weight="fill" />
          <h2 className="text-lg mono">Empirical Rules ({allRules.length})</h2>
          <div className="flex-1" />
          <div className="relative">
            <MagnifyingGlassIcon size={14} color="#64748B" className="absolute left-2 top-2.5" />
            <input
              data-testid="rule-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="filter by family, symbol, symptom..."
              className="pl-7 pr-3 py-1.5 bg-[#15171E] border border-[#2A2D3A] rounded text-xs mono text-[#F1F5F9] w-72"
            />
          </div>
        </div>

        {Object.keys(byFamily).length === 0 && (
          <div className="text-[#64748B] text-sm mono">
            No rules match. Try "Re-mine now" after a cycle to populate.
          </div>
        )}

        {Object.entries(byFamily).map(([fam, rules]) => (
          <div key={fam} className="mb-6" data-testid={`family-${fam}`}>
            <h3 className="text-sm mono uppercase tracking-widest text-[#8B5CF6] mb-2">{fam} ({rules.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {rules.map((r, i) => {
                const conf = r.confidence || 0;
                const barPct = Math.min(100, conf * 100);
                const advColor = r.advice === "increase" ? "#2ECA7F" :
                                 r.advice === "decrease" ? "#FF4D4D" :
                                 r.advice === "preserve_range" ? "#FBBF24" : "#94A3B8";
                return (
                  <div key={i} className="p-3 bg-[#15171E] border border-[#2A2D3A] rounded" data-testid={`rule-${fam}-${i}`}>
                    <div className="flex items-baseline justify-between">
                      <div className="mono text-sm text-[#F1F5F9]">{r.param}</div>
                      <div className="mono text-xs" style={{ color: advColor }}>{r.advice || "?"}</div>
                    </div>
                    <div className="mono text-[10px] text-[#94A3B8] mt-1">{r.condition}</div>
                    <div className="mt-2 h-1.5 bg-[#0B0D14] rounded overflow-hidden">
                      <div className="h-full" style={{ width: `${barPct}%`, background: advColor }} />
                    </div>
                    <div className="mono text-[10px] text-[#64748B] mt-1 flex justify-between">
                      <span>conf {conf.toFixed(2)}</span>
                      <span>n={r.sample_size || "?"}</span>
                      <span>{r.source}</span>
                    </div>
                    {r.winner_growth_median != null && (
                      <div className="mono text-[10px] text-[#2ECA7F] mt-1">
                        winners median growth: {r.winner_growth_median}%
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <PulseIcon size={20} color="#22D3EE" weight="fill" />
          <h2 className="text-lg mono">Brain-Generated Strategies ({strategies.length})</h2>
        </div>
        {strategies.length === 0 ? (
          <div className="text-[#64748B] text-sm mono">
            None yet. Hit <code>POST /api/admin/brain/create-strategy</code> or the Insights CLI.
          </div>
        ) : (
          <div className="space-y-2">
            {strategies.map((s) => (
              <div key={s.id} className="p-3 bg-[#15171E] border border-[#2A2D3A] rounded" data-testid={`gen-strat-${s.id}`}>
                <div className="flex items-baseline justify-between">
                  <div className="mono text-sm text-[#F1F5F9]">{s.class_name}</div>
                  <div className="flex gap-2 items-center">
                    <span className="mono text-[10px] text-[#8B5CF6]">{s.family_tag}</span>
                    <span className={`mono text-[10px] px-2 py-0.5 rounded ${
                      s.status === "live" ? "bg-[#2ECA7F]/20 text-[#2ECA7F]" :
                      s.status === "compiled" ? "bg-[#22D3EE]/20 text-[#22D3EE]" :
                      s.status === "failed" ? "bg-[#FF4D4D]/20 text-[#FF4D4D]" :
                      "bg-[#64748B]/20 text-[#94A3B8]"
                    }`}>{s.status}</span>
                  </div>
                </div>
                <div className="mono text-xs text-[#94A3B8] mt-1">{s.description}</div>
                <div className="mono text-[10px] text-[#64748B] mt-1">
                  goal: {s.goal?.substring(0, 120)}{s.goal && s.goal.length > 120 ? "..." : ""}
                </div>
                <div className="mono text-[10px] text-[#64748B] mt-1">
                  target: {s.target_symbol || "any"} · created {new Date(s.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
